import { useMemo } from "react";

export const useAggregatedValidation = ({ validationStates }) => {
  return useMemo(() => {
    // Check if any validation is currently searching
    const isAnySearching = validationStates.some((state) => state.isSearching);

    // Check if any validation has an error
    const hasAnyError = validationStates.some(
      (state) =>
        state.error ||
        (state.validationResult && !state.validationResult.isValid)
    );

    // Check if all validations are complete (not searching and have results)
    const allValidationsComplete = validationStates.every(
      (state) =>
        !state.isSearching &&
        (state.validationResult !== null || state.error !== null)
    );

    // Get all error messages with their validation steps
    const errorMessages = validationStates
      .map((state) => {
        if (state.error) {
          return {
            message: state.error,
            validationStep: state.validationStep || "unknown",
          };
        }
        if (state.validationResult && !state.validationResult.isValid) {
          return {
            message: state.validationResult.message,
            validationStep:
              state.validationResult.validationStep ||
              state.validationStep ||
              "unknown",
          };
        }
        return null;
      })
      .filter(Boolean);

    // Group errors by validation step
    const errorsByStep = errorMessages.reduce((acc, error) => {
      const step = error.validationStep;
      if (!acc[step]) {
        acc[step] = [];
      }
      acc[step].push(error.message);
      return acc;
    }, {});

    // Get steps that have errors
    const stepsWithErrors = Object.keys(errorsByStep);

    // Check if a specific step has errors
    const hasErrorInStep = (stepName) => {
      return stepsWithErrors.includes(stepName);
    };

    // Get errors for a specific step
    const getErrorsForStep = (stepName) => {
      return errorsByStep[stepName] || [];
    };

    // Check if any validation in a specific step is searching
    const isStepSearching = (stepName) => {
      return validationStates.some(
        (state) => state.isSearching && state.validationStep === stepName
      );
    };

    // Check if a specific step should be disabled for submission
    const shouldDisableStep = (stepName) => {
      return hasErrorInStep(stepName) || isStepSearching(stepName);
    };

    // Button should be disabled if:
    // 1. Any validation is searching, OR
    // 2. Any validation has an error, OR
    // 3. Not all validations are complete
    const shouldDisableButton = isAnySearching || hasAnyError;

    return {
      isAnySearching,
      hasAnyError,
      allValidationsComplete,
      errorMessages: errorMessages.map((error) => error.message), // Keep backward compatibility
      errorMessagesWithSteps: errorMessages, // New detailed error messages
      errorsByStep,
      stepsWithErrors,
      shouldDisableButton,

      // Step-specific methods
      hasErrorInStep,
      getErrorsForStep,
      isStepSearching,
      shouldDisableStep,

      // Summary for debugging
      summary: {
        totalValidations: validationStates.length,
        searchingCount: validationStates.filter((s) => s.isSearching).length,
        errorCount: errorMessages.length,
        completeCount: validationStates.filter(
          (s) =>
            !s.isSearching && (s.validationResult !== null || s.error !== null)
        ).length,
        stepsWithErrorsCount: stepsWithErrors.length,
        errorsByStepSummary: Object.keys(errorsByStep).map((step) => ({
          step,
          errorCount: errorsByStep[step].length,
        })),
      },
    };
  }, [validationStates]);
};
